import { Component, ReactNode } from 'react';
import { CHUNK_RELOAD_KEY } from '../config/constants';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  error: Error | null;
  info: { componentStack?: string } | null;
}

// Class component because hooks can't catch render errors. When something inside
// `children` throws during render, React calls componentDidCatch and we swap to a
// visible error UI instead of letting the error unmount the whole React tree
// (which is what produces the all-black "page blanked out" symptom).
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    this.setState({ info });

    // Safety net for chunk-load errors not already handled by lazyWithRetry
    // (e.g. a lazy import added without lazyWithRetry, or a nested dynamic import).
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      /failed to fetch dynamically imported module|loading chunk/i.test(error.message ?? '');
    if (isChunkError && sessionStorage.getItem(CHUNK_RELOAD_KEY) !== '1') {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
      return;
    }

    console.error('[ErrorBoundary] caught render error:', error, info?.componentStack);
    // Auto-copy a full diagnostic blob to the clipboard so the user can paste it
    // back during support — minified prod stacks are hard to read by eye.
    try {
      const blob = [
        `URL: ${window.location.href}`,
        `UserAgent: ${navigator.userAgent}`,
        `Time: ${new Date().toISOString()}`,
        ``,
        `${error.name}: ${error.message}`,
        ``,
        `STACK:`,
        error.stack || '(no stack)',
        ``,
        `COMPONENT STACK:`,
        info?.componentStack || '(no component stack)',
      ].join('\n');
      navigator.clipboard?.writeText(blob).catch(() => {});
    } catch { /* clipboard not available */ }
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{
        padding: 24, margin: 24, borderRadius: 8,
        background: '#1f1410', border: '1px solid #c2410c',
        color: '#fed7aa', fontSize: 13, fontFamily: 'monospace',
        maxWidth: '100%', overflow: 'auto',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#fb923c' }}>
          {this.props.fallbackTitle || 'Something went wrong rendering this page'}
        </div>
        <div style={{ marginBottom: 12, color: '#fed7aa' }}>
          {error.name}: {error.message}
        </div>
        {error.stack && (
          <details style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', color: '#fdba74', marginBottom: 6 }}>Stack trace</summary>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: '#fed7aa', margin: 0 }}>{error.stack}</pre>
          </details>
        )}
        {info?.componentStack && (
          <details style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', color: '#fdba74', marginBottom: 6 }}>Component stack</summary>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: '#fed7aa', margin: 0 }}>{info.componentStack}</pre>
          </details>
        )}
        <button
          onClick={this.reset}
          style={{
            background: '#c2410c', color: '#fff', border: 'none',
            padding: '6px 14px', borderRadius: 4, cursor: 'pointer',
            fontSize: 12, fontWeight: 600,
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}
